import { FC, FormEvent, useState } from 'react';

import { Dialog } from '@mui/material';

import { useRecoilValue } from 'recoil';

import { useCompanyActivityLog } from '@hooks_iqfu/common/useCompanyActivityLog';

import { companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { GrowthToolServiceKey } from '@type-def_iqfu/dashboard/GrowthTool';

import styles from './GrowthToolModal.module.scss';
import { growthToolServices } from './growthToolServices';

type Props = {
  serviceKey: GrowthToolServiceKey;
  onClose: () => void;
};

const GrowthToolModal: FC<Props> = ({ serviceKey, onClose }) => {
  const service = growthToolServices[serviceKey];
  const { companyId } = useRecoilValue(companyLoginUserDataSelector);
  const { sendActivityLog } = useCompanyActivityLog();

  const [contactName, setContactName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Closing mid-submit would unmount the dialog and drop the post-await setState calls
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedContactName = contactName.trim();
    if (!trimmedContactName) {
      setErrorMessage('ご担当者名を入力してください');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    // The Slack notification is the inquiry's only delivery path, so failures must surface to the user
    const result = await sendActivityLog({
      event_name: 'growth_cv_click',
      properties: {
        company_id: companyId,
        service: service.name,
        contact_name: trimmedContactName,
      },
      notify: true,
    });
    setIsSubmitting(false);

    if (result.ok) {
      setIsCompleted(true);
    } else {
      setErrorMessage('送信に失敗しました。時間をおいて再度お試しください');
    }
  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="growth_tool_modal_title">
      <div className={styles.header}>
        <h2 id="growth_tool_modal_title" className={styles.service_name}>
          {service.name}
        </h2>
        <p className={styles.tagline}>{service.tagline}</p>
        <button type="button" className={styles.close_button} onClick={handleClose} aria-label="閉じる">
          ×
        </button>
      </div>
      <div className={styles.body}>
        <p className={styles.description}>{service.description}</p>
        <ul className={styles.points}>
          {service.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <p className={styles.recommended}>
          <span className={styles.recommended_label}>こんな会社様に:</span>
          {service.recommendedFor.join('/')}
        </p>
        {isCompleted ? (
          <div className={styles.completed}>
            <div className={styles.completed_check}>✓</div>
            <p className={styles.completed_text}>
              お問い合わせを受け付けました。
              <br />
              営業担当よりご連絡いたします。
            </p>
          </div>
        ) : (
          <>
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label className={styles.form_label} htmlFor="growth_tool_contact_name">
                ご担当者名
              </label>
              <input
                id="growth_tool_contact_name"
                type="text"
                className={styles.form_input}
                maxLength={32}
                placeholder="例:山田 太郎"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              {errorMessage && <p className={styles.error_message}>{errorMessage}</p>}
              <button type="submit" className={styles.submit_button} disabled={isSubmitting}>
                {isSubmitting ? '送信中...' : '詳細を問い合わせる'}
              </button>
            </form>
            <p className={styles.note}>{service.note}</p>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default GrowthToolModal;
